/**
 * How safe the library is where it currently lives.
 *
 * A browser may evict an origin's storage when the device runs low — and inside
 * an iOS webview it will. markbird answers that in two ways: a native build
 * keeps every note as a file in the app's own folder (lib/native), and on the
 * web it asks for *persistent* storage, which takes the library out of the
 * evictable bucket. Browsers grant it silently to an installed or well-used app
 * and refuse it elsewhere, so the answer is worth showing rather than assuming.
 */

export interface StorageHealth {
  /** Whether the browser promises not to evict this origin's data. */
  persisted: boolean;
  /** Bytes in use and available, when the browser will say. */
  usage: number | null;
  quota: number | null;
  /** False when the browser has no Storage API at all (Safari < 15.2, old Firefox). */
  supported: boolean;
}

interface StorageManagerLike {
  persisted?: () => Promise<boolean>;
  persist?: () => Promise<boolean>;
  estimate?: () => Promise<{ usage?: number; quota?: number }>;
}

function manager(): StorageManagerLike | null {
  return (navigator as unknown as { storage?: StorageManagerLike }).storage ?? null;
}

export async function storageHealth(): Promise<StorageHealth> {
  const storage = manager();
  if (!storage?.persisted) return { persisted: false, usage: null, quota: null, supported: false };
  type Estimate = { usage?: number; quota?: number };
  const [persisted, estimate] = await Promise.all([
    storage.persisted().catch(() => false),
    storage.estimate?.().catch((): Estimate => ({})) ?? Promise.resolve<Estimate>({}),
  ]);
  return {
    persisted,
    usage: estimate.usage ?? null,
    quota: estimate.quota ?? null,
    supported: true,
  };
}

/**
 * Ask the browser to keep this library. Safe to call repeatedly: it returns the
 * current answer when permission was already decided, and asking inside a click
 * is what gets a prompt-based browser to decide at all.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  const storage = manager();
  if (!storage?.persist) return false;
  if (await storage.persisted?.().catch(() => false)) return true;
  return storage.persist().catch(() => false);
}
